import compact from "lodash/compact";
import { TemplateKey } from "PFApp/constants/templates";
import { Accordion, AccordionState } from "PFComponents/accordion";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline, Stack } from "PFComponents/containers";
import { Tile } from "PFComponents/tile";
import { fetchActivitiesOptions } from "PFCore/services/activities";
import { ActivityOption } from "PFCore/types";
import { Option } from "PFTypes/options";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useNotificationsPanelContext } from "../contexts/notifications_panel_context";
import css from "../notifications_panel.module.scss";
import { ActivitySelect } from "./activity_select";
import { GroupBySelect } from "./group_by_select";
import { NotificationTypeSelect } from "./notification_type_select";

type NotificationsPanelFiltersProps = {
  ref?: React.Ref<HTMLDivElement>;
  disabled: boolean;
  onAccordionStateChange: (state: AccordionState) => void;
};

const ROLE_TEMPLATE_KEYS = [TemplateKey.Role, TemplateKey.AuditRole];
const ENGAGEMENT_TEMPLATE_KEYS = [TemplateKey.Engagement, TemplateKey.AuditEngagement];

export const NotificationsPanelFilters = ({
  ref,
  disabled,
  onAccordionStateChange
}: NotificationsPanelFiltersProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "notifications.filters" });
  const { categories, setCategories, engagement, setEngagement, role, setRole } =
    useNotificationsPanelContext();
  const [isRoleValidating, setIsRoleValidating] = useState(false);

  const handleEngagementChange = async ({ original: activity }: Option<ActivityOption>) => {
    setEngagement(activity);

    if (role) {
      setIsRoleValidating(true);
      const selectedRoleQuery = await fetchActivitiesOptions({
        templateKey: ROLE_TEMPLATE_KEYS,
        activitiesWithNotifications: true,
        parentActivityId: activity.id,
        id: [role.id]
      });

      if (!selectedRoleQuery.entries.length) {
        setRole(undefined);
      }
      setIsRoleValidating(false);
    }
  };
  const handleRoleChange = ({ original: activity }: Option<ActivityOption>) => setRole(activity);

  const handleClearFilters = (e) => {
    e.stopPropagation();
    setCategories([]);
    setEngagement(undefined);
    setRole(undefined);
  };

  const filtersCounter = compact([categories.length ? categories : undefined, engagement, role]).length;

  return (
    <Tile ref={ref} className={css.filters} paddingVariant="lg">
      <Accordion
        header={
          <Inline as="span" spread>
            <span>{t("title", { count: filtersCounter })}</span>
            {!!filtersCounter && <ActionIcon name="filter-clean" size="sm" onClick={handleClearFilters} />}
          </Inline>
        }
        iconSet="chevron"
        headerVariant="bodyBold"
        defaultState="closed"
        onStateChange={onAccordionStateChange}
        headerFullWidth
      >
        <Stack>
          <ActivitySelect
            label={t("engagement")}
            activity={engagement}
            onChange={handleEngagementChange}
            onClear={engagement && (() => setEngagement(undefined))}
            templateKeys={ENGAGEMENT_TEMPLATE_KEYS}
            disabled={disabled}
          />
          <ActivitySelect
            label={t("role")}
            activity={role}
            onChange={handleRoleChange}
            onClear={role && (() => setRole(undefined))}
            templateKeys={ROLE_TEMPLATE_KEYS}
            parentActivityId={engagement?.id}
            disabled={disabled || isRoleValidating}
          />
          <NotificationTypeSelect disabled={disabled} />
          <GroupBySelect disabled={disabled} />
        </Stack>
      </Accordion>
    </Tile>
  );
};
